import { applyTransaction, op } from "libs/transaction";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import * as ops from "libs/operations";
import { RecordValue, getMapRecord, getMapRecords, getPointer } from "libs/schema";

/* -------------------------------------------------------------------------------------------------
 *  updateThreadRequestLastResolvedAt
 * -------------------------------------------------------------------------------------------------
 */

type UpdateThreadRequestLastResolvedAtProps = {
  threadId: string;
  requestId: string;
  lastResolvedAt: {
    sent_at: string;
    timeline_order: string;
  } | null;
};

export const updateThreadRequestLastResolvedAt = createAction<UpdateThreadRequestLastResolvedAtProps>(
  "threadRequest.updateThreadRequestLastResolvedAt",
  {
    operations: async (props, context) => {
      const [_, { recordMap }] = await context.environment.loader.getThreadRequests({
        thread_id: props.threadId,
        recipient_user_id: context.currentUserId,
      });

      const request = getMapRecord(recordMap, {
        table: "thread_request",
        id: props.requestId,
      });

      if (!request) {
        throw new InvalidActionStateError("[resolveThreadRequest] No request found", {
          errorData: { props },
        });
      }

      const updateThreadRequestLastResolvedAtOps = ops.threadRequest.updateThreadRequestLastResolvedAt({
        request,
        lastResolvedAt: props.lastResolvedAt,
      });

      const transaction = op.transaction({
        authorId: context.currentUserId,
        operations: updateThreadRequestLastResolvedAtOps,
      });

      // Note that this mutates the recordMap as well as the records within it (e.g. the `request` object,
      // fetched above, is mutated).
      applyTransaction({
        recordMap,
        currentTimestamp: new Date().toISOString(),
        isServer: false,
        useActionVersioning: true,
        transaction,
      });

      const updatedRequests = getMapRecords(recordMap, "thread_request");

      // Calculate the new notification `respond_by` values after this request change.
      const notificationUpd = updatedRequests.reduce(
        (store, request) => {
          // If the request is resolved, we don't need to update the notification.
          if (request.resolved_at) return store;

          if (!store.request_priority || !store.respond_by || !store.respond_by_tz) {
            store.request_priority = request.priority;
            store.respond_by = request.respond_by;
            store.respond_by_tz = request.respond_by_tz;
            return store;
          }

          store.request_priority =
            request.priority < store.request_priority ? request.priority : store.request_priority;

          if (request.respond_by < store.respond_by) {
            store.respond_by = request.respond_by;
            store.respond_by_tz = request.respond_by_tz;
          }

          return store;
        },
        {
          request_priority: null,
          respond_by: null,
          respond_by_tz: null,
        } as Pick<RecordValue<"notification">, "request_priority" | "respond_by" | "respond_by_tz">,
      );

      // Users can only resolve requests made of them and notifications only cache request data for
      // requests made of that user. This means that, when a user resolves a request, they only impact
      // their own notification. Also note that a notification should always already exist for this thread,
      // since as part of creating a request we also create a notification (if it doesn't already exist).
      transaction.operations.push(
        op.update(
          getPointer("notification", {
            user_id: context.currentUserId,
            thread_id: props.threadId,
          }),
          notificationUpd,
        ),
      );

      return transaction.operations;
    },
    authorize: async (_props, context) => {
      context.environment.logger.warn("threadRequest.resolveThreadRequest.authorize not yet implemented!");
    },
  },
);

export const actions = {
  updateThreadRequestLastResolvedAt,
};
